import { ConversationChannelTypeExtended } from '@provision/chat-utils';
import { AnimationDefinition, Transition, useReducedMotion, Variants } from 'framer-motion';
import { RefObject, useCallback, useMemo, useState } from 'react';
import { useComposerStartFoldedSetting } from 'src/graphql/entities/user-setting-value';
import { useChatInputStore } from 'src/store/chat-input';
import { useNotifications } from 'src/utils/hooks';
import { ComposerStartFoldedOffer } from './ComposerStrip/composer-strip';
import { InputFieldHandle } from './InputField/input-field';

// The third fold of a session earns the one-time "Start folded next time?" offer.
const COMPOSER_FOLD_OFFER_THRESHOLD = 3;

type ComposerFoldVariant = 'open' | 'folded';

const FOLD_EASE = [0.2, 0.7, 0.2, 1] as const;
const CSS_EASE = [0.25, 0.1, 0.25, 1] as const;
const FOLD_TRANSITION: Transition = {
  duration: 0.3,
  ease: FOLD_EASE,
  opacity: { duration: 0.22, ease: CSS_EASE },
};
// the strip fades in a beat after the card starts folding out
const STRIP_SHOW_TRANSITION: Transition = {
  ...FOLD_TRANSITION,
  opacity: { duration: 0.22, ease: CSS_EASE, delay: 0.05 },
};
const REDUCED_MOTION_TRANSITION: Transition = { duration: 0 };

const CARD_VARIANTS: Variants = {
  open: { height: 'auto', opacity: 1, y: '0rem' },
  folded: { height: 0, opacity: 0, y: '0.625rem' },
};

const STRIP_VARIANTS: Variants = {
  open: { height: 0, opacity: 0, y: '0.5rem' },
  folded: { height: 'auto', opacity: 1, y: '0rem' },
};

interface ComposerFoldMotionProps {
  initial: false;
  animate: ComposerFoldVariant;
  variants: Variants;
  transition: Transition;
  onAnimationComplete?: (definition: AnimationDefinition) => void;
}

interface UseComposerFoldParams {
  isChatReadOnly: boolean;
  inputFieldRef: RefObject<InputFieldHandle | null>;
}

interface UseComposerFoldResult {
  isFolded: boolean;
  /** Folded, or mid fold/unfold animation: the dock follows its content instead of the drag height. */
  isFolding: boolean;
  /** Fully folded: the card is out of layout, kept mounted so the editor keeps its draft. */
  isCollapsed: boolean;
  fold: () => void;
  unfold: (messageType?: ConversationChannelTypeExtended) => void;
  offer: ComposerStartFoldedOffer | null;
  cardMotionProps: ComposerFoldMotionProps;
  stripMotionProps: ComposerFoldMotionProps;
}

export function useComposerFold({
  isChatReadOnly,
  inputFieldRef,
}: UseComposerFoldParams): UseComposerFoldResult {
  const chatInputCollapsed = useChatInputStore.use.chatInputCollapsed();
  const setChatInputCollapsed = useChatInputStore.use.setChatInputCollapsed();
  const composerFoldCount = useChatInputStore.use.composerFoldCount();
  const incrementComposerFoldCount = useChatInputStore.use.incrementComposerFoldCount();
  const [composerStartFolded, setComposerStartFolded] = useComposerStartFoldedSetting();
  const { showSuccessAlert } = useNotifications();
  const prefersReducedMotion = useReducedMotion() === true;

  // A fold lasts the session; a user who never folded starts from their saved preference.
  const isFolded = !isChatReadOnly && (chatInputCollapsed ?? composerStartFolded === true);

  const [isSettledFolded, setIsSettledFolded] = useState(isFolded);
  const [isEditorFocusPending, setIsEditorFocusPending] = useState(false);

  // A read-only state panel unmounts the card mid-state, and a remounted card starts settled
  // without an animation to report it.
  if (isChatReadOnly && isSettledFolded) {
    setIsSettledFolded(false);
  }

  const onCardAnimationComplete = useCallback(
    (definition: AnimationDefinition) => {
      if (definition === 'folded') {
        setIsSettledFolded(true);
        return;
      }
      if (definition !== 'open') return;
      setIsSettledFolded(false);
      // an element still growing cannot take focus, so it lands once the card is fully open
      if (isEditorFocusPending) {
        inputFieldRef.current?.focusEditor();
        setIsEditorFocusPending(false);
      }
    },
    [inputFieldRef, isEditorFocusPending],
  );

  const fold = useCallback(() => {
    setIsEditorFocusPending(false);
    setChatInputCollapsed(true);
    incrementComposerFoldCount();
  }, [setChatInputCollapsed, incrementComposerFoldCount]);

  // The mode switches before the card starts growing, so it opens already in the mode the
  // strip button named; a macro, which already set the mode, passes none.
  const unfold = useCallback(
    (messageType?: ConversationChannelTypeExtended) => {
      if (messageType) inputFieldRef.current?.switchMessageType(messageType);
      setIsEditorFocusPending(true);
      setChatInputCollapsed(false);
    },
    [inputFieldRef, setChatInputCollapsed],
  );

  const offer = useMemo<ComposerStartFoldedOffer | null>(() => {
    if (
      composerStartFolded !== undefined ||
      composerFoldCount < COMPOSER_FOLD_OFFER_THRESHOLD
    ) {
      return null;
    }
    return {
      onAccept: () => {
        setComposerStartFolded(true);
        showSuccessAlert('Done. Change it any time in Preferences.');
      },
      onDecline: () => setComposerStartFolded(false),
    };
  }, [composerStartFolded, composerFoldCount, setComposerStartFolded, showSuccessAlert]);

  const variant: ComposerFoldVariant = isFolded ? 'folded' : 'open';

  return {
    isFolded,
    isFolding: isFolded || isSettledFolded,
    isCollapsed: isFolded && isSettledFolded,
    fold,
    unfold,
    offer,
    cardMotionProps: {
      initial: false,
      animate: variant,
      variants: CARD_VARIANTS,
      transition: prefersReducedMotion ? REDUCED_MOTION_TRANSITION : FOLD_TRANSITION,
      onAnimationComplete: onCardAnimationComplete,
    },
    stripMotionProps: {
      initial: false,
      animate: variant,
      variants: STRIP_VARIANTS,
      transition: getStripTransition(prefersReducedMotion, isFolded),
    },
  };
}

function getStripTransition(prefersReducedMotion: boolean, isFolded: boolean): Transition {
  if (prefersReducedMotion) return REDUCED_MOTION_TRANSITION;
  return isFolded ? STRIP_SHOW_TRANSITION : FOLD_TRANSITION;
}
